/**
 * datetime-local inputs hold wall-clock time in the browser's zone; the API
 * stores and returns UTC instants. These helpers are the only conversions —
 * sending the raw input value made the server read it as UTC (Dentour 261008:
 * a task typed for 13:10 in Rome showed 15:10).
 */

/** datetime-local is a wall-clock value in the user's explicitly labelled device zone.
 * Reject DST gaps instead of silently moving a patient commitment to a different hour. */
export function localCommitmentToIso(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return null
  const [year, month, day, hour, minute] = value.split(/[-T:]/).map(Number)
  if (
    date.getFullYear() !== year ||
    date.getMonth() + 1 !== month ||
    date.getDate() !== day ||
    date.getHours() !== hour ||
    date.getMinutes() !== minute
  )
    return null
  return date.toISOString()
}

/** The inverse: a stored instant ("…Z" / "+00:00") as a datetime-local value
 * in the device zone. Slicing the ISO string instead shows the UTC clock. */
export function isoToLocalCommitment(value: string | null | undefined): string {
  if (!value) return ''
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** A datetime-local value as the instant to send. A DST-gap time falls back to
 * the browser's own resolution rather than dropping the date. */
export function localInputToIso(value: string): string | null {
  if (!value) return null
  const exact = localCommitmentToIso(value)
  if (exact) return exact
  const date = new Date(value)
  return Number.isFinite(date.getTime()) ? date.toISOString() : null
}
